import React, { forwardRef, useEffect, useState } from 'react';
import { Input as HeroInput } from '@heroui/react';

export interface CustomInputProps {
  label?: string;
  placeholder?: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isClearable?: boolean;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  validate?: (value: string) => string | null;
  onChange?: (val: string) => void;
  onClear?: () => void;
  value?: string | number;
  name?: string;
  type?:
    | 'text'
    | 'email'
    | 'number'
    | 'search'
    | 'url'
    | 'tel'
    | 'date'
    | 'time';
  size?: 'sm' | 'md' | 'lg' | 'md+';
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  step?: string;
  min?: number;
  max?: number;
  onChangeCheckBeforeSettingValue?: (any: any) => any;
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  (
    {
      label = '',
      placeholder = '',
      description = '',
      errorMessage = '',
      isRequired = true,
      isDisabled = false,
      isClearable = true,
      startContent,
      endContent,
      validate,
      onChange,
      onClear,
      value: propValue = '',
      type = 'text',
      name = '',
      size = 'md',
      onBlur,
      onKeyDown,
      step,
      min,
      max,
      onChangeCheckBeforeSettingValue,
      ...rest
    },
    ref,
  ) => {
    const [value, setValue] = useState(propValue);
    const [error, setError] = useState<string | null>(null);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      setValue(propValue);
    }, [propValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (min != undefined && parseFloat(newValue) < min) {
        return;
      }
      if (max != undefined && parseFloat(newValue) > max) {
        return;
      }
      if (onChangeCheckBeforeSettingValue) {
        const result = onChangeCheckBeforeSettingValue(newValue);

        setValue(result as string);
      } else setValue(newValue);

      if (validate) {
        const validationError = validate(newValue);
        setError(validationError);
      }

      onChange?.(newValue);
    };

    // Determine if we should use the custom md+ size
    const isCustomMdPlus = size === 'md+';
    // Use 'md' for HeroUI's Input when our custom size is 'md+'
    const heroUISize = isCustomMdPlus ? 'md' : size;

    return (
      <HeroInput
        ref={ref}
        name={name}
        value={value}
        onChange={handleChange}
        label={label ? <span style={{ color: '#6e6e6e' }}>{label}</span> : null}
        placeholder={placeholder}
        description={
          description ? (
            <span style={{ color: '#6e6e6e' }}>{description}</span>
          ) : null
        }
        errorMessage={error || errorMessage}
        isInvalid={!!error || !!errorMessage}
        isRequired={isRequired}
        isDisabled={isDisabled}
        isClearable={isClearable}
        type={type}
        onFocus={() => {
          setIsFocused(true);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          onBlur?.(e as React.FocusEvent<HTMLInputElement>);
        }}
        onKeyDown={onKeyDown}
        size={heroUISize}
        step={step}
        color="default" // ✅ default color
        variant="bordered" // ✅ border variant
        radius={size == 'sm' ? 'sm' : 'md'} // ✅ 2nd radius option
        labelPlacement="outside" // ✅ label outside
        startContent={startContent}
        endContent={endContent}
        onClear={onClear}
        classNames={{
          inputWrapper: [
            'border-divider',
            'data-[hover=true]:border-secondary',
            'data-[focus=true]:border-primary',
            isFocused ? 'shadow-medium' : '',
            'transition-all duration-200',
            isCustomMdPlus ? 'h-[44px] min-h-[44px]' : '',
          ].join(' '),
          input:
            value && value?.length > 0
              ? 'text-default-500'
              : 'placeholder:text-[#9e9e9e]',
          label: 'text-sm',
        }}
        {...rest}
      />
    );
  },
);

CustomInput.displayName = 'CustomInput';
export default CustomInput;
